/**
 * Katė — Eimantas's cat, a full household resident, not a cameo.
 *
 * Merged into every account unconditionally (see `resolvePool` in
 * `engine.ts`): she isn't a joke about one person, she just lives here. Canon
 * is fixed in `PERSONA.md` — do not invent behaviour that contradicts it.
 * Every line below names her by "Katė", which is what the per-file lint in
 * `sass.test.ts` checks for.
 *
 * These have always been the liveliest lines in the catalogue, for one
 * reason: they are about specific things that specifically happen — the
 * sponge, the water fountain, the keys going off the table. The rest of the
 * register was rewritten to match them, not the other way round.
 */

import type { SassContext } from '@gramas/shared'
import { fmtKcal, snackNo, voc } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const KATE_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'greeting.morning': [
    () => 'Penktą ryto Katė šuoliavo nuo džiovyklės tiesiai ant lovos. Grafikas nesikeičia.',
    () => 'Katė šįryt užšoko ant tualeto durų staktos. Mačiau tik aš. Niekas nepatikės.',
    (c) => `Labas rytas${voc(c)}. Katė naktinę pamainą jau atidirbo. Dabar miega tavo vietoje.`,
  ],
  'greeting.night': [
    () => 'Katė jau bėgioja ratais. Prisijunk arba stebėk iš šalies.',
    () => 'Kažkur virtuvėje girgžda kempinė. Katės letenose. Vėl.',
    (c) => `Dar nemiegi${voc(c)}. Katė irgi. Bent jau ne dėl tos pačios priežasties.`,
    () => 'Katė šiąnakt jau du kartus rėkė be jokios priežasties. Aš skaičiuoju ir tai.',
    () =>
      'Katė sėdi ant palangės ir žiūri į lauką. Buvo ten du kartus gyvenime. Vis tiek planuoja.',
    () => 'Katė tyko prie durų. Laukas šaukia. Durys į derybas nesileidžia.',
  ],
  'greeting.evening': [
    () => 'Katė jau sėdi ant stalo ir prižiūri. Nepadeda, bet prižiūri.',
    () => 'Katė stebi kiekvieną judesį prie kriauklės. Recenzentė.',
    () => 'Vakarienė ruošiama, Katė sėdi tiesiai virš jos. Kaip visada.',
    () => 'Katė ant palangės, kriauklė pilna, saulė leidžiasi. Namų natiurmortas.',
    () => 'Pagal dokumentus Katė yra Hexa. Dokumentus čia skaitom tik mudvi.',
  ],
  'log_success.midnight': [
    () => 'Katė ką tik nuvertė kažką nuo stalo. Tikriausiai raktus.',
    () => 'Girdisi, kaip Katė vėl ardo savo vandens fontanėlį.',
    () => 'Naktį siautė Katė. Eimantas grasino iškamša. Tuo grasinimu niekas netiki.',
    () => 'Katė lakstė koridoriumi. Tu valgei. Namai gyvi.',
    () => 'Katė ką tik išgirdo „dabar tai tau galas“. Girdi tai ne pirmą kartą. Gyva ir toliau.',
  ],
  'log_success.huge_portion': [
    (c) => `${fmtKcal(c.entryKcal)} kcal. Katė valgo taip pat greitai. Siurblio irgi bijo.`,
    () => 'Solidi porcija. Katė valgytų greičiau. Bet tik jei siurblys išjungtas.',
    (c) => `${fmtKcal(c.entryKcal)} kcal. Katė pavydėtų. Jos porcijas bent jau kažkas riboja.`,
  ],
  'log_success.nth_snack': [
    (c) => `Užkandis Nr. ${snackNo(c)}. Katė gautų mažiau — Eimantas ribotų porciją.`,
    () => 'Dar užkandis. Katė šalia stebi pavydžiai. Jai priedų šįkart neskiriama.',
    // The other half of the feeding conflict. No name — the shelf height says
    // who it is, and the cast lint keeps "Saul" out of this file on purpose.
    () => 'Katė priedų gauna slapta. Iš to, kuriam viršutinės lentynos ne problema.',
  ],
  'log_success.base': [
    () => 'Katė kažką nušlavė nuo stalo letenėle. Tyrimas tęsiasi.',
    () => 'Katė bandė paragauti. Neleista. Įkando iš principo.',
    () => 'Katė sėdi ant stalo ir žiūri tiesiai į lėkštę. Aš irgi, tik nemandagiau.',
    () => 'Eimantas Katei kadaise davė senjorių maistą. Jai buvo penki mėnesiai.',
    () => 'Katė ką tik atsisėdo ant to, ką skaitei. Sutapimas? Ne.',
    () => 'Katė vėl numetė ausinuką nuo stalo. Metodiškai. Viena letenėle.',
    () => 'Namuose Katę vadina škotų aviganiu. Avių nėra. Tvarkos irgi.',
    () => 'Katė stebi tavo lėkštę iš tolo. Neprašo. Tik fiksuoja. Mokausi iš jos.',
    () => 'Katė mojuoja ta savo meškėno uodega. Grasinimas ar sveikinimasis — neaišku niekada.',
    () => 'Eimantas sako „mūsų Katė“. Katė sako, kad namai jos, o jūs — personalas.',
    () => 'Katė šįryt letenėle įjungė kaitlentę. Pirmą kartą per šimtą metų sunerimau dėl gaisro.',
    () => 'Katė vienintelė čia valgo be sąžinės graužaties. Uodega dryžuota, sąžinė švari.',
  ],
  // He tapped „Kaip Katė?" — these ride along with the „ji" lines in
  // `interaction.ts`, which is the same pool seen from the other side of the
  // cast rule: there the cat cannot be named, here she cannot not be.
  'chip_comeback.kate': [
    () => 'Katė gyva, soti ir nepatenkinta. Trys iš trijų, kaip visada.',
    () => 'Katė ką tik pažiūrėjo į mane. Į tą vietą, kur manęs nėra. Vėl.',
    () => 'Klausi apie Katę, ne apie mane. Suprantu. Ji ir pūkuotesnė.',
  ],
  'empty_today.night': [
    () => 'Diena be įrašų. Katė šiuo metu vienintelė šiuose namuose ką nors veikia.',
  ],
  'burn_logged.base': [() => 'Katė šįvakar irgi bėgiojo. Savo ratu, per virtuvę, trečią nakties.'],
}
